import * as React from 'react';
import { observer } from 'mobx-react';
import { TextArea } from '@payscale/design';
import { DecisionDispatcher } from 'nomst/Stores/Decision/decision.dispatcher';
import { WorkflowAction } from 'nomst/Stores/Decision/Constants/workflowAction';
import { WorkflowComment } from 'nomst/Stores/Decision/workflowComment';
import { ManagerWorkflowStore } from 'nomst/Stores/Decision/managerWorkflow.store';
import { ReportsView } from 'typings/enums';
import { User } from 'nomst/Stores/User/user';
import * as displayStrings from 'nomst/Components/Decision/workflowDialogDisplayStrings';
import * as dateFns from 'date-fns';
import './workflowDialog.scss';

export interface IWorkflowDialogProps {
    decisionDispatcher: DecisionDispatcher;
    managerWorkflowStore: ManagerWorkflowStore;
    teamView: ReportsView;
    currentUser: User;
    isMultiManager: boolean;
}

export class WorkflowDialog extends React.Component<IWorkflowDialogProps> {
    get dialogTitle(): string {
        const { managerWorkflowStore, currentUser } = this.props;
        // TODO hardcoding to the first one until we address this
        const isCurrentUserTeamManager =
            managerWorkflowStore.isCurrentUserTeamManager(managerWorkflowStore.managerSourceIds[0], currentUser);

        if (managerWorkflowStore.workflowAction === WorkflowAction.SubmitReview) {
            if (managerWorkflowStore.isOverBudget && isCurrentUserTeamManager) {
                return displayStrings.submitReviewOwnTeamOverBudgetDialogTitle;
            } else if (managerWorkflowStore.isOverBudget && !isCurrentUserTeamManager) {
                return displayStrings.submitReviewOtherTeamOverBudgetDialogTitle;
            } else {
                return displayStrings.submitReviewDialogTitle;
            }
        }

        if (managerWorkflowStore.workflowAction === WorkflowAction.SendBack) {
            return displayStrings.sendBackDialogTitle;
        }

        if (managerWorkflowStore.workflowAction === WorkflowAction.Approve) {
            return displayStrings.approveDialogTitle;
        }

        return '';
    }

    get isNextWorkflowTextHidden(): boolean {
        const { currentUser } = this.props;
        return currentUser.isSupportPersonnel;
    }

    private get isSendReviewDisabled(): boolean {
        const { managerWorkflowStore } = this.props;
        if (this.isCommentRequired && managerWorkflowStore.comment.trim().length === 0) {
            return true;
        }

        return false;
    }

    private get isCommentRequired(): boolean {
        const { managerWorkflowStore } = this.props;
        return managerWorkflowStore.workflowAction === WorkflowAction.SubmitReview
            && managerWorkflowStore.isOverBudget
            && managerWorkflowStore.requireCommentWhenOverBudget;
    }

    private get submitButtonText(): string {
        const { managerWorkflowStore } = this.props;
        if (managerWorkflowStore.workflowAction === WorkflowAction.SubmitReview) {
            return displayStrings.submitReviewButtonText;
        }

        if (managerWorkflowStore.workflowAction === WorkflowAction.SendBack) {
            return displayStrings.sendBackButtonText;
        }

        if (managerWorkflowStore.workflowAction === WorkflowAction.Approve) {
            return displayStrings.approveButtonText;
        }

        return '';
    }

    constructor(props: IWorkflowDialogProps) {
        super(props);
        this.handleCloseDialog = this.handleCloseDialog.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
        this.handleCommentChange = this.handleCommentChange.bind(this);
        this.renderComment = this.renderComment.bind(this);
    }

    render(): React.ReactNode {
        const { managerWorkflowStore } = this.props;
        if (managerWorkflowStore.workflowAction === WorkflowAction.None ||
            !managerWorkflowStore.managerWorkflows) {
            return null;
        }

        return (
            <div className="pxl-modal nomst-workflow-confirmation">
                <div className="pxl-modal__content side-modal">
                    <button
                        className="no-btn-style"
                        onClick={this.handleCloseDialog}
                    >
                        <i className="icon-cancel" />
                    </button>
                    <h2 className="nomst-workflow-confirmation__headline">
                        {this.dialogTitle}
                    </h2>
                    {this.renderTeamAlert()}
                    {this.renderNextWorkflowText()}
                    {this.renderCurrentWorkflowText()}
                    {this.renderInputCommentForm()}
                    <div className="btn-options">
                        <button className="pxl-link" onClick={this.handleCloseDialog}>
                            I&apos;m not ready
                        </button>
                        <button
                            onClick={this.handleSubmit}
                            disabled={this.isSendReviewDisabled}
                            className="pxl-btn pxl-btn--primary submit-btn"
                        >
                            {this.submitButtonText}
                        </button>
                    </div>
                    {this.renderComments()}
                </div>
            </div>
        );
    }

    renderAllEmployeesAlert(): React.ReactNode {
        const { managerWorkflowStore } = this.props;
        if (managerWorkflowStore.workflowAction === WorkflowAction.SendBack) {
            return null;
        }
        const allEmployeesText = managerWorkflowStore.workflowAction === WorkflowAction.SubmitReview ?
            displayStrings.allTeamsForwardText : displayStrings.allTeamsApprovedText;
        return (
            <p className="nomst-workflow-confirmation__alert">
                <i className="icon-warning"/>
                {allEmployeesText}
            </p>
        );
    }

    renderTeamAlert(): React.ReactNode {
        const { managerWorkflowStore, teamView, isMultiManager } = this.props;
        if (teamView === ReportsView.AllReports) {
            return this.renderAllEmployeesAlert();
        }
        if (!isMultiManager) {
            return null;
        }
        let alertText;
        switch (managerWorkflowStore.workflowAction) {
            case WorkflowAction.SubmitReview:
                alertText = displayStrings.teamsForwardText;
                break;
            case WorkflowAction.Approve:
                alertText = displayStrings.teamsApprovedText;
                break;
            case WorkflowAction.SendBack:
                alertText = displayStrings.teamsBackText;
                break;
        }
        return (
            <p className="nomst-workflow-confirmation__alert">
                <i className="icon-warning"/>
                {managerWorkflowStore.selectedManagerWorkflows.length} {alertText}
            </p>
        );
    }

    renderNextWorkflowText(): React.ReactNode {
        const { managerWorkflowStore } = this.props;
        let bodyText;
        switch (managerWorkflowStore.workflowAction) {
            case WorkflowAction.SubmitReview:
                bodyText = displayStrings.nextWorkflowForwardDescriptionText;
                break;
            case WorkflowAction.SendBack:
                bodyText = displayStrings.nextWorkflowBackDescriptionText;
                break;
            case WorkflowAction.Approve:
                bodyText = displayStrings.nextWorkflowApproveDescriptionText;
                break;
        }
        return (
            <div
                className="workflow-next-steps"
                hidden={this.isNextWorkflowTextHidden}
            >
                <h4>What&apos;s next</h4>
                <p className="workflow-next-steps__body">
                    {bodyText}
                </p>
            </div>
        );
    }

    private handleCloseDialog(): void {
        const { managerWorkflowStore } = this.props;
        managerWorkflowStore.resetWorkflowSubmission();
    }

    private async handleSubmit(): Promise<void> {
        const { decisionDispatcher, currentUser, managerWorkflowStore } = this.props;
        const { selectedManagerWorkflows } = managerWorkflowStore;
        if (!selectedManagerWorkflows || !selectedManagerWorkflows.length) {
            return;
        }
        // There's a subtlety here. If we're approving, we only need to tell it to handle
        // the first event since it loops through everything. You would think we have to
        // loop, but you'd be wrong!
        await decisionDispatcher.dispatchProceedWorkflowSubmissionAsync(
            currentUser,
            managerWorkflowStore.getCurrentUserAsReviewerIndex(selectedManagerWorkflows[0], currentUser),
            selectedManagerWorkflows[0].currentReviewerIndex);
    }

    private handleCommentChange(_: undefined, value: string): void {
        const { managerWorkflowStore } = this.props;
        managerWorkflowStore.changeComment(value);
    }

    private renderCurrentWorkflowText(): React.ReactNode {
        const { managerWorkflowStore, isMultiManager } = this.props;
        const { selectedManagerWorkflows } = managerWorkflowStore;
        if (managerWorkflowStore.workflowAction === WorkflowAction.SubmitReview) {
            const nextReviewerName =
                isMultiManager ? 'Multiple Reviewers' : selectedManagerWorkflows[0].nextReviewerDisplayName;
            return (
                <h3 className="nomst-workflow-confirmation__next-step-reviewer">
                    Next Reviewer <strong>{nextReviewerName}</strong>
                </h3>
            );
        }

        if (managerWorkflowStore.workflowAction === WorkflowAction.SendBack) {
            const previousReviewerName =
                isMultiManager ? 'Multiple Reviewers' : selectedManagerWorkflows[0].previousReviewerDisplayName;
            return (
                <h3 className="nomst-workflow-confirmation__next-step-reviewer">
                    Send back to <strong>{previousReviewerName}</strong>
                </h3>
            );
        }

        return null;
    }

    private renderInputCommentForm(): React.ReactNode {
        const { managerWorkflowStore } = this.props;
        return (
            <div>
                {this.renderInputCommentTitle()}
                <TextArea
                    id="manager-comment"
                    rows={4}
                    cols={20}
                    value={managerWorkflowStore.comment}
                    onChange={this.handleCommentChange}
                    placeholder="What do you want the next reviewer to know about your thought process?"
                />
            </div>
        );
    }

    private renderInputCommentTitle(): React.ReactNode {
        if (this.isCommentRequired) {
            return (
                <label htmlFor="manager-comment" className="pxl-control-label">
                    {displayStrings.mandatoryCommentText}
                </label>
            );
        }

        return (
            <label htmlFor="manager-comment" className="pxl-control-label">
                Comments <span className="optional-comment">(optional)</span>
            </label>
        );
    }

    private renderComments(): React.ReactNode {
        const { selectedManagerWorkflows } = this.props.managerWorkflowStore;
        return (
            <div className="team-comments">
                <h3>
                    <i className="icon-users" />Team Comments
                </h3>
                <div className="comments">
                    {selectedManagerWorkflows
                        .map(w => w.workflowComments.map(this.renderComment))
                    }
                </div>
            </div>
        );
    }

    private renderComment(comment: WorkflowComment, index: number): React.ReactNode {
        return (
            <div className="comment" key={index}>
                {this.renderCommentIcon(comment)}
                <h4>{comment.reviewerDisplayName}</h4>
                <span>{this.getCommentTypeText(comment)}</span>
                &nbsp;&middot;&nbsp;
                <span>{this.getCommentDateText(comment)}</span>
                <p>{comment.comment}</p>
            </div>
        );
    }

    private renderCommentIcon(comment: WorkflowComment): React.ReactNode {
        if (comment.type === WorkflowAction.SubmitReview) {
            return (
                <i className="icon-flipped icon-paper-plane submit-icon" />
            );
        }

        if (comment.type === WorkflowAction.SendBack) {
            return (
                <i className="icon-lock-open-alt send-back-icon" />
            );
        }

        return null;
    }

    private getCommentTypeText(comment: WorkflowComment): string {
        if (comment.type === WorkflowAction.SubmitReview) {
            return 'Sent for review';
        }

        if (comment.type === WorkflowAction.SendBack) {
            return 'Sent back';
        }

        return '';
    }

    private getCommentDateText(comment: WorkflowComment): string {
        const dateDifference = dateFns.differenceInCalendarDays(
            new Date(),
            comment.commentDateType);

        if (dateDifference === 0) {
            return 'Today';
        }

        if (dateDifference === 1) {
            return 'Yesterday';
        }

        return dateFns.format(comment.commentDateType, 'MMM d, yyyy');
    }
}

export default observer(WorkflowDialog);
